/**
 * Pricing and quota, fetched from the backend so no page declares its own numbers.
 *
 * The fallback below is the same table the backend serves (tier-config.js DEFAULT_CONFIGS). It
 * exists only so a page still renders when the backend is unreachable — if you change a price,
 * change it in tier-config.js, and mirror it here and in extension/src/shared/config.js.
 */

import { BACKEND_URL } from './config.js';

export const PRICING_FALLBACK = {
  currency: 'USD',
  tiers: [
    {
      tier: 'starter',
      label: 'Free',
      price_cents: 0,
      price_cents_annual: 0,
      prompts_per_month: 25,
      fair_use_per_month: null,
      protocols: ['clarity', 'context'],
      features: ['basic_enhancement']
    },
    {
      tier: 'professional',
      label: 'Pro',
      price_cents: 1200,
      price_cents_annual: 9900,
      prompts_per_month: -1,
      fair_use_per_month: 1000,
      protocols: ['comprehensive', 'clarity', 'context', 'specificity', 'collaboration', 'formatting'],
      features: ['basic_enhancement', 'advanced_protocols', 'history', 'priority_support']
    }
  ],
  anonymous: {
    per30Days: 3,
    windowMs: 30 * 24 * 60 * 60 * 1000,
    protocols: ['clarity', 'context']
  }
};

let cached = null;
let inFlight = null;

export async function getPricing() {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = fetch(`${BACKEND_URL}/api/config/pricing`)
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(data => {
      cached = data;
      return data;
    })
    .catch(error => {
      console.warn('⚠️ Pricing fetch failed, using bundled table:', error.message);
      return PRICING_FALLBACK;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Synchronous read for first paint; the fetched table replaces it once it lands. */
export function getPricingSync() {
  return cached || PRICING_FALLBACK;
}

export function getTier(pricing, tier) {
  return pricing.tiers.find(t => t.tier === tier) || null;
}

export function formatPrice(cents) {
  if (!cents) return '$0';
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** "Save 31%" — the number on the annual toggle, never typed by hand. */
export function annualSavingPercent(tier) {
  if (!tier?.price_cents || !tier?.price_cents_annual) return 0;
  return Math.round((1 - tier.price_cents_annual / (tier.price_cents * 12)) * 100);
}

export function formatQuota(tier) {
  return tier.prompts_per_month === -1
    ? 'Unlimited enhancements'
    : `${tier.prompts_per_month} enhancements a month`;
}
